import { useState, useCallback, useRef } from 'react';
import { engineFetchJson } from '@/lib/engineHttp';
import type { WidgetDefinition, WidgetProps } from '@/types/widget';

interface SearchResult {
  id: string;
  title: string;
  type: string;
  snippet?: string;
  url?: string;
}

type SearchPayload = Record<string, unknown>;
type SearchResponse =
  | SearchPayload[]
  | {
      results?: SearchPayload[];
      chunks?: SearchPayload[];
    };

function QuickSearchWidget({ context }: WidgetProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [endpointStatus, setEndpointStatus] = useState<'unknown' | 'available' | 'unavailable'>('unknown');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const doSearch = useCallback(
    (q: string) => {
      if (!q.trim()) {
        setResults(null);
        return;
      }
      setSearching(true);

      // Try the codex search endpoint first
      void engineFetchJson<SearchResponse>(`/codex/search?q=${encodeURIComponent(q)}&limit=10`)
        .then((data) => {
          const rawItems = Array.isArray(data) ? data : data.results ?? data.chunks ?? [];
          const items: SearchResult[] = rawItems.map((item, i) => {
            const id = (item.chunk_id as string) ?? (item.id as string) ?? `r-${i}`;
            const snippet = (item.text as string) ?? (item.content_preview as string) ?? (item.content as string) ?? (item.snippet as string) ?? '';
            const title = (item.title as string) ?? snippet.slice(0, 80) ?? 'Untitled';
            const type = (item.source as string) ?? (item.source_type as string) ?? (item.type as string) ?? 'codex';
            return { id, title, type, snippet };
          });
          setResults(items);
          setEndpointStatus('available');
        })
        .catch(() => {
          setEndpointStatus('unavailable');
          setResults([]);
        })
        .finally(() => {
          setSearching(false);
        });
    },
    [context.authToken],
  );

  const handleInput = (value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(value), 400);
  };

  return (
    <div className="sp-widget">
      <div className="sp-widget-header">
        <span>Quick Search</span>
      </div>
      <div className="sp-widget-body" style={{ padding: '4px 12px' }}>
        <input
          type="text"
          className="sp-input"
          placeholder="Search Codex, tasks, sources..."
          value={query}
          onChange={(e) => handleInput(e.target.value)}
          style={{ marginBottom: 8 }}
        />

        {searching && (
          <div style={{ color: 'var(--sp-text-muted)', fontSize: 12 }}>Searching...</div>
        )}

        {endpointStatus === 'unavailable' && query.trim() && (
          <div>
            <div style={{ color: 'var(--sp-warn)', fontSize: 12, marginBottom: 4 }}>
              Search service not yet wired
            </div>
            <div className="mono" style={{ color: 'var(--sp-text-dim)', fontSize: 11, marginBottom: 2 }}>
              GET /api/v1/codex/search?q=...
            </div>
            <div style={{ fontSize: 11, color: 'var(--sp-text-dim)', marginTop: 8 }}>
              Universal search will query Codex chunks, tasks, sources, and settings
              when the search endpoint ships in the engine.
            </div>
          </div>
        )}

        {endpointStatus === 'available' && results && results.length === 0 && query.trim() && !searching && (
          <div style={{ color: 'var(--sp-text-muted)', fontSize: 12, padding: '4px 0' }}>
            No results for &ldquo;{query}&rdquo;
          </div>
        )}

        {results && results.length > 0 && (
          <div style={{ maxHeight: 200, overflow: 'auto' }}>
            {results.map((r) => (
              <div
                key={r.id}
                style={{
                  padding: '6px 0',
                  fontSize: 12,
                  borderBottom: '1px solid var(--sp-border)',
                  cursor: r.url ? 'pointer' : 'default',
                }}
                onClick={() => {
                  if (r.url) window.location.href = r.url;
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span className="mono" style={{ fontSize: 9, color: 'var(--sp-accent)', textTransform: 'uppercase' }}>
                    {r.type}
                  </span>
                  <span style={{ color: 'var(--sp-text)', fontWeight: 500 }}>
                    {r.title}
                  </span>
                </div>
                {r.snippet && (
                  <div style={{ color: 'var(--sp-text-muted)', fontSize: 11, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.snippet}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {!query.trim() && !results && (
          <div style={{ color: 'var(--sp-text-dim)', fontSize: 11 }}>
            Type to search across your workspace.
          </div>
        )}
      </div>
    </div>
  );
}

export const quickSearchWidget: WidgetDefinition = {
  id: 'quick-search',
  name: 'Quick Search',
  description: 'Universal search across Codex, tasks, and sources.',
  icon: 'search',
  category: 'universal',
  scope: 'both',
  defaultSize: { w: 6, h: 4 },
  minSize: { w: 3, h: 3 },
  component: QuickSearchWidget,
};
